import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { FieldLabel, PaddingInset } from '@cash-design-system/react'

import { FormMaskedInput } from '../FormMaskedInput'

type SsnInputProps = {
  variant: 'FULL' | 'LAST_FOUR'
  name: string
  isRequired?: boolean
}

const SecurityText = styled.span`
  ${({ theme }) => theme.typography.metaText};
  color: ${props => props.theme.colors.disabledLabel};
`

export function SsnInput({ variant, name, isRequired }: SsnInputProps) {
  if (variant === 'LAST_FOUR') {
    return <LastFourSsnInput name={name} isRequired={isRequired} />
  }

  return <FullSsnInput name={name} isRequired={isRequired} />
}

function FullSsnInput({ name, isRequired }: Omit<SsnInputProps, 'variant'>) {
  const { t } = useTranslation()

  // Full SSNs have 9 digits.
  const lengthValidation = {
    value: 9,
    message: t('Enter a valid SSN.', {
      context: 'error message for a form field with an invalid social security number',
    }),
  }

  return (
    <FieldLabel label={t('SSN', { context: 'label for form field asking for a social security number' })}>
      <FormMaskedInput
        name={name}
        format="###-##-####"
        placeholder={t('9-digit SSN', {
          context: 'placeholder text for a form field asking for a social security number',
        })}
        rules={{
          required: {
            value: !!isRequired,
            message: t('Enter your SSN.', {
              context: 'error message for form field when missing a social security number',
            }),
          },
          minLength: lengthValidation,
          maxLength: lengthValidation,
        }}
      />
      <PaddingInset>
        <SecurityText>
          {t('Secured with 256-bit encryption', {
            context: 'social security number form field description information',
          })}
        </SecurityText>
      </PaddingInset>
    </FieldLabel>
  )
}

function LastFourSsnInput({ name, isRequired }: Omit<SsnInputProps, 'variant'>) {
  const { t } = useTranslation()

  const lengthValidation = {
    value: 4,
    message: t('Enter the last 4 of your SSN.', {
      context:
        'error message for form field when the last 4 digits of a social security number are the incorrect number of digits',
    }),
  }

  return (
    <FieldLabel
      label={t('Last 4 digits of SSN', {
        context: 'label for form field asking for last 4 digits of a social security number',
      })}
    >
      <FormMaskedInput
        name={name}
        format="####"
        placeholder={t('1234', {
          context: 'placeholder for form field asking for the last 4 digits of a social security number',
        })}
        rules={{
          required: {
            value: !!isRequired,
            message: t('Enter the last 4 digits of your SSN.', {
              context: 'error message for form field when missing the last 4 digits of a social security number',
            }),
          },
          minLength: lengthValidation,
          maxLength: lengthValidation,
        }}
      />
      <PaddingInset>
        <SecurityText>
          {t('Secured with 256-bit encryption', {
            context: 'social security number form field description information',
          })}
        </SecurityText>
      </PaddingInset>
    </FieldLabel>
  )
}
